import { useActions } from 'ActionsContext';
import { useJobs } from 'JobContext';
import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Article } from '../../models/Article';
import { StyledButton } from '../styles/StyledButtons';
import { StyledHeading } from '../styles/styles';
import ArticleApprovalPreview from './articleApproval/ArticleApprovalPreview';
import ArticleRefinementChatPanel from './articleApproval/ArticleRefinementChatPanel';
import { shouldOnlyShowChat } from './articleApproval/articleApprovalUtils';
import { useArticleApproval } from './articleApproval/useArticleApproval';

interface ArticleApprovalProps {
  article: Article;
}

function ArticleApprovalContent({ article }: ArticleApprovalProps) {
  const navigate = useNavigate();
  const jobContext = useJobs();
  const { refreshActions } = useActions();

  const {
    loading,
    content,
    isProcessing,
    chatHistory,
    helpModalOpen,
    chatInputValue,
    chatWindowCollapsed,
    hasRunningJob,
    selectedModel,
    runningJob,
    chatMessagesRef,
    expandedChatHeight,
    setHelpModalOpen,
    setChatInputValue,
    setChatWindowCollapsed,
    setSelectedModel,
    publishArticle,
    archiveArticle,
    unarchiveArticle,
    downloadOriginalArticle,
    handleRefinementSubmit,
    handleRegenerate,
  } = useArticleApproval({
    article,
    jobContext,
    navigate,
    refreshActions,
  });

  if (loading) {
    return (
      <Box sx={{ pt: 5, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (shouldOnlyShowChat(article.status)) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <StyledHeading>Article Refinement</StyledHeading>
            <StyledButton onClick={downloadOriginalArticle}>Original Article</StyledButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Status: {article.status} - Use the AI chat below to refine this article
          </Typography>
        </Box>

        <ArticleRefinementChatPanel
          chatWindowCollapsed={chatWindowCollapsed}
          onToggleChatWindow={() => setChatWindowCollapsed((collapsed) => !collapsed)}
          isProcessing={isProcessing}
          hasRunningJob={hasRunningJob}
          runningJob={runningJob}
          chatHistory={chatHistory}
          selectedModel={selectedModel}
          onSelectedModelChange={setSelectedModel}
          chatInputValue={chatInputValue}
          onChatInputChange={setChatInputValue}
          helpModalOpen={helpModalOpen}
          onHelpModalOpen={() => setHelpModalOpen(true)}
          onHelpModalClose={() => setHelpModalOpen(false)}
          onRefinementSubmit={handleRefinementSubmit}
          chatMessagesRef={chatMessagesRef}
          expandedChatHeight={expandedChatHeight}
        />
      </Box>
    );
  }

  if (!content) {
    return (
      <Box sx={{ pt: 5, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          pb: `${chatWindowCollapsed ? 80 : 400}px`,
        }}
      >
        <ArticleApprovalPreview
          article={article}
          content={content}
          isProcessing={isProcessing}
          hasRunningJob={hasRunningJob}
          onDownloadOriginal={downloadOriginalArticle}
          onPublish={publishArticle}
          onArchive={archiveArticle}
          onUnarchive={unarchiveArticle}
          onRegenerate={handleRegenerate}
        />
      </Box>

      <ArticleRefinementChatPanel
        chatWindowCollapsed={chatWindowCollapsed}
        onToggleChatWindow={() => setChatWindowCollapsed((collapsed) => !collapsed)}
        isProcessing={isProcessing}
        hasRunningJob={hasRunningJob}
        runningJob={runningJob}
        chatHistory={chatHistory}
        selectedModel={selectedModel}
        onSelectedModelChange={setSelectedModel}
        chatInputValue={chatInputValue}
        onChatInputChange={setChatInputValue}
        helpModalOpen={helpModalOpen}
        onHelpModalOpen={() => setHelpModalOpen(true)}
        onHelpModalClose={() => setHelpModalOpen(false)}
        onRefinementSubmit={handleRefinementSubmit}
        chatMessagesRef={chatMessagesRef}
        expandedChatHeight={expandedChatHeight}
      />
    </Box>
  );
}

export default ArticleApprovalContent;
